import { useAppDispatch } from '@/hooks';
import { Button, Tag, TimePicker, Tooltip } from 'antd';
import locale from 'antd/es/date-picker/locale/ja_JP';
import { SortOrder } from 'antd/es/table/interface';
import dayjs, { Dayjs } from 'dayjs';
import lodash, { isNil } from 'lodash';
import React, { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { StatusCategory } from '@/pages/project-management/task/models';
import { STATUS_NAME } from '@pages/project-management/detail/constant';
import { renderTaskStatus } from '@pages/project-management/detail/utils/helper';
import { DEFAULT_FILTER_FORM, TYPE_OF_TASK } from '@pages/project-management/project-list-v2/constant';
import { IFormFilter, IPrimary } from '@pages/project-management/project-list-v2/models';
import { IStatus } from '@pages/project-management/project-list/models';
import { PrimaryFilter } from '@pages/task/components';

import { BaseButton } from '@/components/base-button/BaseButton';
import BaseModal from '@/components/base-modal/BaseModal';
import { showBasePopup } from '@/components/base-popup/BasePopup';
import BaseRadioGroups from '@/components/common/base-radio';
import { FormInputDate } from '@/components/form-input-date/FormInputDate';
import { SelectCustom } from '@/components/form-select/SelectCustom';
import AppTooltip, { styleTooltip } from '@components/app-tooltip/AppTooltip';
import FormBaseFilter, { RefActionControl } from '@components/base-filter';
import { FetchColumnsType, IObject } from '@components/common/table-base';
import TableFetch, { IDefaultSort } from '@components/common/table-fetch';
import { TagTaskType } from '@components/common/task/TagTaskType';

import { setAlertNotification } from '@/redux/globalReducer';

import useAuthorization from '@hooks/useAuthorization';
import useFetch from '@hooks/useFetch';
import { useFilterConfigs } from '@hooks/useFilterConfigs';
import useMutation from '@hooks/useMutation';
import useOptionsGlobal from '@hooks/useOptionsGlobal';

import { fetchFilterConfigs } from '@/utils/helpers/routeLoaders';
import { getUsersInfo } from '@/utils/services/ProjectApiService';
import { ANTD_TO_QUERY_SORT, CATEGORY, CATEGORY_OPTIONS, QUERY_SORT, ROUTER_IDS } from '@utils/constants';
import { API } from '@utils/constants/Apis';
import { FORMAT_DATE_EN, FORMAT_TIME, TYPE } from '@utils/constants/AppConstants';
import { HTTP_STATUS_CODE } from '@utils/constants/Http';
import { VIEW_PROJECT_URL, VIEW_TASK_OF_PROJECT_URL } from '@utils/constants/RouteContants';
import { DataViewer } from '@utils/helpers/common';
import { convertFullWidthToHalfWidth, formatDateTime } from '@utils/helpers/globalHelper';
import { ITaskPic, IUserProfile } from '@utils/interfaces/user';

import { generateFilter } from '../utils';

type PrimaryKey = keyof Omit<IPrimary, 'isDraft'>;

export type PicInfo = ITaskPic & { label?: string };
export const TaskList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { user, isInternalRole, isExternalRole } = useAuthorization();
  const { data: filterConfigs, update: updateFilterConfigs } = useFilterConfigs<FieldValues>(ROUTER_IDS.TASK_LIST);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [enableDeadline, setEnableDeadline] = useState<'status' | 'deadline'>('status');
  const [filter, setFilter] = useState({
    filter: generateFilter({
      projectPicId: (filterConfigs?.picBy === TYPE_OF_TASK.ME || !filterConfigs?.picBy) && isInternalRole ? user?.id : null,
      ...filterConfigs?.primary,
      categoryId: isExternalRole ? CATEGORY.OTHER : filterConfigs?.primary?.categoryId
    }),
    textSearch: filterConfigs?.search ?? '',
    filterTaskPics: filterConfigs?.primary?.picIds?.join(';'),
    assignedTasks: isInternalRole && filterConfigs?.picBy === TYPE_OF_TASK.SPECIAL
  });
  const filterTableRef = useRef<RefActionControl>({
    toggle: () => {}
  });
  const tableRef = useRef<{ refreshData: () => void; clearSelection: () => void }>();

  const { countryOptions } = useOptionsGlobal();
  const { data: statuses = [] } = useFetch<IStatus[]>(`${API.GET_PROJECT_STATUS_LIST}?type=task_list`, 'GET');
  const { data: statusOption = [] } = useFetch<IStatus[]>(`${API.GET_PROJECT_STATUS_LIST}?type=task`, 'GET');
  const { mutate: updateStatus } = useMutation('/prj/projects/{idProject}/task/{idTask}/status', {
    method: 'PUT',
    bodyType: 'json',
    showToastError: true,
    showToastSuccess: true,
    defaultSuccessMsg: t('common:MSG_C_003', { item: t('button:keep') }) ?? ''
  });
  const { mutate: updateMultiTask } = useMutation(API.UPDATE_MULTI_TASKS(), {
    method: 'PUT',
    bodyType: 'json',
    showToastError: true,
    showToastSuccess: true,
    defaultSuccessMsg: t('common:MSG_C_003', { item: t('button:submit') }) ?? ''
  });

  const DEFAULT_FORM_FILTER_VALUES = {
    picBy: TYPE_OF_TASK.ME,
    search: '',
    primary: {
      ...DEFAULT_FILTER_FORM.primary,
      categoryId: isExternalRole ? [CATEGORY.OTHER] : null
    }
  };
  const methods = useForm<IFormFilter & { updatedDateFrom?: Dayjs | null; updatedDeadlineTime?: Dayjs | null }>({
    defaultValues: DEFAULT_FORM_FILTER_VALUES
  });
  const { watch, setValue, reset, getValues } = methods;
  const [watchPicBy, watchPrimary, watchSearch] = watch(['picBy', 'primary', 'search']);
  const watchDeadlineTime = watch('updatedDeadlineTime');
  const [picListInfo, setPicListInfo] = useState<PicInfo[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IObject[]>([]);
  const [updatedStatuses, setUpdatedStatuses] = useState<Record<string, string>>({});
  const [isWarningDeadline, setIsWarningDeadline] = useState(false);
  const [isDeadlineRequiredError, setIsDeadlineRequiredError] = useState(false);
  const handleBulkUpdatePopoverOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setEnableDeadline('status');
        setIsDeadlineRequiredError(false);
        return;
      }

      setValue('updatedDateFrom', null);
      setValue('updatedDeadlineTime', null);
      setIsDeadlineRequiredError(false);
    },
    [setValue]
  );

  const fetchPicInfo = async (picIds: string[]) => {
    const { data: users } = await getUsersInfo(picIds);
    return (
      users?.map((user) => ({
        ...user,
        id: user.picId,
        value: user.picId,
        label: !user?.status ? `(${t('account_list:status_account:inactive')})${user.name}` : user.name
      })) ?? []
    );
  };

  const validateFilterPic = async (configs: FieldValues | null) => {
    try {
      const picIds = configs?.primary?.picIds;
      if (!configs || !picIds?.length) return configs;
      const newData = await fetchPicInfo(picIds);
      setPicListInfo(newData);
      return configs;
    } catch (error) {
      return configs;
    }
  };

  const handleChangeStatus = async (value: any, task: IObject) => {
    const status = statusOption?.find((item) => item.id === value.value);
    if (!status) return;
    if (!task?.deadLine) {
      setIsWarningDeadline?.(true);
      return;
    }

    const response = await handleShowPopup();
    if (response !== 'confirm') {
      throw new Error('cancel');
    }

    const result = await updateStatus({ statusId: value.value }, { idProject: String(task.projectId), idTask: String(task.id) });
    if (result?.status !== HTTP_STATUS_CODE.SUCCESS) {
      throw new Error('cancel');
    }

    setUpdatedStatuses((currentStatuses) => ({
      ...currentStatuses,
      [String(task.id)]: value.value
    }));
    tableRef.current?.refreshData();

    return value;
  };

  const handleShowPopup = async () => {
    filterTableRef.current?.toggle(false);
    return showBasePopup({
      type: TYPE.CONFIRM,
      title: t('master_data:task:title_confirm_popup') || '',
      msg: t('master_data:task:description_confirm_popup') ?? ''
    });
  };

  const handleShowPopupMultiTask = async () => {
    filterTableRef.current?.toggle(false);
    return showBasePopup({
      type: TYPE.CONFIRM,
      title: t('master_data:task:title_confirm_popup_multi') || '',
      msg: t('master_data:task:description_confirm_popup_multi') ?? ''
    });
  };

  const handleUpdateMultiTask = async () => {
    const updatedDate = getValues('updatedDateFrom');
    const updatedDeadlineTime = getValues('updatedDeadlineTime');
    const isDeadlineUpdate = enableDeadline === 'deadline';
    const completedStatus = statusOption?.find((item) => item.name === STATUS_NAME.COMPLETED);

    if (isDeadlineUpdate && !updatedDate) {
      setIsDeadlineRequiredError(true);
      return;
    }

    const tasksToUpdate = isDeadlineUpdate ? selectedRows : selectedRows.filter((task) => task?.deadLine);
    const skippedTasks = !isDeadlineUpdate && tasksToUpdate.length < selectedRows.length;

    if (!tasksToUpdate.length) {
      if (skippedTasks) {
        dispatch(
          setAlertNotification({
            show: true,
            type: 'warning',
            message: t('common:MSG_P_047')
          })
        );
      }
      return;
    }

    const response = await handleShowPopupMultiTask();
    if (response === 'cancel') {
      filterTableRef.current?.toggle(true);
      return;
    }
    if (response !== 'confirm') return;

    const result = await updateMultiTask({
      tasks: tasksToUpdate.map((task) => ({
        taskId: task.id,
        vesion: task.version,
        projectId: task.projectId
      })),
      statusId: isDeadlineUpdate ? null : completedStatus?.id ?? null,
      deadline: isDeadlineUpdate && updatedDate ? dayjs(updatedDate).format('YYYY-MM-DD') : null,
      deadlineTime: isDeadlineUpdate && updatedDeadlineTime ? dayjs(updatedDeadlineTime).format('HH:mm') : null
    });

    if (result?.status !== HTTP_STATUS_CODE.SUCCESS) return;

    if (!isDeadlineUpdate && completedStatus) {
      setUpdatedStatuses((currentStatuses) => {
        const nextStatuses = { ...currentStatuses };
        tasksToUpdate.forEach((task) => {
          nextStatuses[String(task.id)] = completedStatus.id;
        });
        return nextStatuses;
      });
    }

    if (skippedTasks) {
      dispatch(
        setAlertNotification({
          show: true,
          type: 'warning',
          message: t('common:MSG_P_047')
        })
      );
    }

    setSelectedKeys([]);
    setSelectedRows([]);
    tableRef.current?.clearSelection();
    tableRef.current?.refreshData();
  };

  useEffect(() => {
    if (!filterConfigs || initialLoaded) {
      return;
    }
    (async () => {
      const sanitizeFilterConfigs = await validateFilterPic(filterConfigs);

      reset({
        ...DEFAULT_FORM_FILTER_VALUES,
        ...sanitizeFilterConfigs
      });
      setInitialLoaded(true);
    })();
  }, [filterConfigs, initialLoaded]);

  const COLUMNS: FetchColumnsType<IObject> = [
    {
      title: t('task:deadline'),
      key: 'deadLine',
      width: 150,
      sorter: true,
      sortOrder: 'ascend',
      render: (data) => (
        <>
          {DataViewer.displayTime(data?.deadLine, FORMAT_DATE_EN)}
          {data?.deadLineTime ? `\u3000${dayjs(data.deadLineTime, 'HH:mm').format(FORMAT_TIME)}` : ''}
        </>
      )
    },
    {
      title: t('task:type'),
      key: 'taskType',
      width: 90,
      render: (data) => <TagTaskType type={data.taskType} className='m-0 w-[62px] text-center h-[24px] leading-[22px]' />
    },
    {
      title: t('task:manager'),
      key: 'taskPics',
      width: 130,
      render: (data) => {
        return (
          <div className='w-fit max-w-full'>
            {data.taskPics?.map((taskPic: IUserProfile) => {
              if (isNil(taskPic?.status)) return '';
              return (
                <div key={taskPic.id} className='truncate'>
                  {!taskPic.status ? `(${t('account_list:status_account:inactive')})` : ''}
                  {taskPic.name}
                </div>
              );
            })}
          </div>
        );
      },
      tooltip: (_value: any, record: any) => {
        return record.taskPics?.map((taskPic: IUserProfile) => {
          if (isNil(taskPic?.status)) return '';
          return (
            <div key={taskPic.id} className='truncate'>
              {!taskPic.status ? `(${t('account_list:status_account:inactive')})` : ''}
              {taskPic.name}
            </div>
          );
        });
      }
    },
    {
      title: t('project:fields:project_id'),
      key: 'projectCode',
      width: 158,
      render: (data) => (
        <p className='truncate'>
          {data.isDraft && <span className='text-negative'>({t('task:draft')})&nbsp;</span>}
          {!data.isPublished && <span className='text-negative'>({t('task:unpublish')})&nbsp;</span>}
          <AppTooltip title={data.projectCode}>
            <Link to={VIEW_PROJECT_URL(data.projectId)}>
              <span className='underline'>{data.projectCode}</span>
            </Link>
          </AppTooltip>
        </p>
      )
    },
    {
      title: t('task:title'),
      key: 'taskName',
      width: 337,
      render: (data) => (
        <>
          {isExternalRole && data.preStatusName === STATUS_NAME.TODO ? (
            <AppTooltip title={data.taskName}>
              <span>{DataViewer.display(data.taskName)}</span>
            </AppTooltip>
          ) : (
            <AppTooltip title={data.taskName}>
              <Link to={VIEW_TASK_OF_PROJECT_URL(data.projectId, data.id)} style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}>
                <span className='underline truncate'>{data.taskName}</span>
              </Link>
            </AppTooltip>
          )}
        </>
      )
    },
    {
      title: t('task:status'),
      key: 'status',
      width: 320,
      render: (data) => {
        if (isExternalRole) {
          return renderTaskStatus(data.status, '!w-auto !inline-flex !px-[8px] !h-[24px] my-auto');
        }

        const currentStatus = updatedStatuses[String(data.id)] ?? data.preStatusName;

        const statusOptions = statusOption
          ?.filter((item) => {
            if (data.taskType === CATEGORY.SUPPORT) {
              return true;
            }

            return !item.types?.includes(StatusCategory.TASK_SUPPORT);
          })
          .map((item) => ({
            value: item.id,
            label: item.name
          }));

        const status = statusOptions?.find((item) => item.value === currentStatus);

        return (
          <div className='flex gap-2 items-center'>
            <div className='mot-pick relative'>
              <SelectCustom
                className='w-full min-w-[160px]'
                labelInValue
                options={statusOptions}
                allowClear={false}
                value={{
                  value: currentStatus,
                  label: status?.label ?? currentStatus
                }}
                preChange={(value) => handleChangeStatus(value, data)}
                key='value'
                title=''
              />
            </div>
            <div>{renderTaskStatus(data.status, '!w-auto !inline-flex !px-[8px] !h-[24px] my-auto')}</div>
          </div>
        );
      }
    },
    {
      title: t('task:applicant_country'),
      key: 'applicantCountryName',
      width: 140,
      render: (data) => (
        <Tooltip title={data.applicantCountryName} {...styleTooltip}>
          <div className='w-fit max-w-full truncate'>{data.applicantCountryName}</div>
        </Tooltip>
      )
    },
    {
      title: t('task:applicant_name'),
      key: 'applicantName',
      width: 140,
      render: (data) => {
        const renderName = (classes?: string) =>
          data.applicantName?.split(/\r?\n/)?.map((name: string, index: number) => (
            <>
              {name && (
                <div key={`applicantName_${index}`} className={classes}>
                  {name}
                </div>
              )}
            </>
          ));
        return (
          <Tooltip title={renderName()} {...styleTooltip}>
            <div className='w-fit max-w-full'>{renderName('truncate')}</div>
          </Tooltip>
        );
      }
    },
    {
      title: t('task:company_name'),
      key: 'assignmentCompanyName',
      width: 337,
      render: (data) => {
        const maxLines = Math.max(data.applicantName?.split(/\r?\n/)?.length ?? 1, data.taskPics?.length ?? 1);
        // use style because of can not use tailwind dynamic className in this case
        const style: CSSProperties =
          maxLines > 1
            ? {
                whiteSpace: 'pre-line',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: maxLines
              }
            : {
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              };

        return (
          <Tooltip title={data.applicantCompanyName} {...styleTooltip}>
            <div className='w-fit max-w-full overflow-hidden' style={style}>
              {DataViewer.display(data.applicantCompanyName)}
            </div>
          </Tooltip>
        );
      }
    }
  ];

  const SECONDARY_FILTER_OPTIONS = [
    {
      label: t('task:my_tasks'),
      value: TYPE_OF_TASK.ME
    },
    {
      label: t('task:all_tasks'),
      value: TYPE_OF_TASK.ALL
    },
    {
      label: t('task:assigned_tasks'),
      value: TYPE_OF_TASK.SPECIAL
    }
  ];

  const SECONDARY_FILTER = isInternalRole
    ? {
        label: SECONDARY_FILTER_OPTIONS.find((option) => option.value === watchPicBy)?.label ?? SECONDARY_FILTER_OPTIONS[0]?.label,
        name: 'picBy',
        options: SECONDARY_FILTER_OPTIONS
      }
    : null;
  const totalFiltered = useMemo(
    () =>
      lodash.filter(lodash.entries(watchPrimary), ([key, value]) => {
        if (key === 'categoryId' && isExternalRole) {
          return false;
        }
        return !lodash.isNil(value) && value !== '' && (!lodash.isArray(value) || value.length > 0);
      }).length,
    [watchPrimary, isExternalRole]
  );

  const getTaskListId = (picBy: string): string => {
    const isTaskMe = picBy === TYPE_OF_TASK.ME;
    const isTaskAll = picBy === TYPE_OF_TASK.ALL;

    if (isTaskMe) return ROUTER_IDS.TASK_LIST;
    if (isTaskAll) return `${ROUTER_IDS.TASK_LIST}_${TYPE_OF_TASK.ALL}`;
    return `${ROUTER_IDS.TASK_LIST}_${TYPE_OF_TASK.SPECIAL}`;
  };

  const fetchFilterData = async (picBy: string) => {
    const listId = getTaskListId(picBy);
    return (await fetchFilterConfigs(listId)) || {};
  };

  const updateFilter = async (data: FieldValues, isChangeView?: boolean) => {
    let primary: any;
    let textSearch;
    const picBy = data?.picBy || watchPicBy;

    filterTableRef.current?.toggle(false);
    if (isChangeView) {
      const res: any = await fetchFilterData(picBy);
      primary = res?.primary || {};

      await validateFilterPic({
        primary
      });

      setValue('primary', {
        ...primary,
        categoryId: primary?.categoryId || DEFAULT_FILTER_FORM.primary.categoryId
      });
      setValue('picBy', picBy);
      setValue('search', res?.search || '');
      textSearch = res.search;

      updateFilterConfigs(
        {
          ...res,
          picBy
        },
        getTaskListId(picBy)
      );
    } else {
      textSearch = data?.search || watchSearch;
      primary = data?.primary || {};
      const defaultCategoryId = data.primary?.categoryId?.length ? data.primary?.categoryId : null;
      updateFilterConfigs(
        {
          ...data,
          picBy,
          search: textSearch,
          primary: {
            ...data.primary,
            categoryId: isExternalRole ? [CATEGORY.OTHER] : defaultCategoryId
          }
        },
        getTaskListId(picBy)
      );
    }
    setFilter({
      filter: generateFilter({
        projectPicId: isInternalRole && picBy === TYPE_OF_TASK.ME ? user?.id : null,
        ...primary,
        categoryId: primary?.categoryId?.length ? primary?.categoryId : DEFAULT_FILTER_FORM.primary.categoryId
      }),
      textSearch: convertFullWidthToHalfWidth(textSearch || '')?.trim(),
      filterTaskPics: primary?.picIds?.join(';'),
      assignedTasks: isInternalRole && (data.picBy || watchPicBy) === TYPE_OF_TASK.SPECIAL
    });
  };

  const resetPrimaryFilter = () => {
    reset({
      ...getValues(),
      primary: {
        ...DEFAULT_FILTER_FORM.primary,
        categoryId: isExternalRole ? [CATEGORY.OTHER] : DEFAULT_FILTER_FORM.primary.categoryId
      }
    });
  };

  const TagItem = ({ label, value, field }: { label: ReactNode; value?: any; field: keyof IPrimary }) => (
    <Tag
      closable
      className='bg-gray2 body-400 tag-item flex items-center m-0 gap-[5px] !h-[24px]'
      onClose={(e) => {
        e.preventDefault();
        const newPrimary = {
          ...getValues('primary'),
          [field]: !value?.length ? null : value
        };
        setValue('primary', newPrimary);
        updateFilter({
          ...getValues(),
          primary: newPrimary
        });
      }}
    >
      {label}
    </Tag>
  );

  const renderTagSection = () => {
    const tagSectionKeys: PrimaryKey[] = ['categoryId', 'countryIds', 'deadlineFrom', 'deadlineTo', 'picIds', 'statuses'];
    const keysHasValue = tagSectionKeys.filter((key) => {
      if (isExternalRole) {
        return key !== 'categoryId' && watchPrimary[key]?.length;
      }
      return watchPrimary[key]?.length;
    });
    if (!keysHasValue.length) {
      return null;
    }
    return (
      <div className='mt-[16px] flex flex-wrap gap-[8px]'>
        {keysHasValue.map((key) => {
          if (key === 'categoryId') {
            const categoryIds = (watchPrimary[key] ?? []) as string[];
            return categoryIds.map((category) => (
              <TagItem
                key={`${key}_${category}`}
                label={
                  category === CATEGORY.OTHER
                    ? t('template_layout:other_category')
                    : category === CATEGORY.SUPPORT
                      ? t('task:type_support')
                      : category
                }
                value={categoryIds.filter((item) => item !== category)}
                field={key}
              />
            ));
          }
          if (['countryIds', 'picIds', 'statuses'].includes(key)) {
            const tagIds = (watchPrimary[key] ?? []) as string[];
            return tagIds.map((id) => {
              const picName = picListInfo?.find((user) => user?.id === id)?.label;
              const countryName = countryOptions?.find((option) => option.value === id)?.label;
              const status = statuses?.find((item) => item.id === id)?.name;
              const label: Record<string, ReactNode> = {
                countryIds: countryName,
                picIds: picName,
                statuses: status
              };
              return <TagItem key={`${key}_${id}`} label={label[key]} value={tagIds.filter((item) => item !== id)} field={key} />;
            });
          }
          const value = watchPrimary[key] as any;
          const label: Record<string, ReactNode> = {
            categoryId: CATEGORY_OPTIONS.find((type) => type.value === value)?.label,
            deadlineFrom: formatDateTime(value),
            deadlineTo: formatDateTime(value)
          };
          return <TagItem key={key} label={label[key]} field={key} />;
        })}
        <Button
          size='small'
          className='tag-btn__clear-all !h-[24px]'
          onClick={(e) => {
            e.preventDefault();
            resetPrimaryFilter();
            updateFilter({
              ...getValues(),
              primary: {
                ...DEFAULT_FILTER_FORM.primary,
                categoryId: isExternalRole ? [CATEGORY.OTHER] : DEFAULT_FILTER_FORM.primary.categoryId
              }
            });
          }}
        >
          {t('button:clear_filter')}
        </Button>
      </div>
    );
  };

  const customParamSort = (sortField: React.Key | null | undefined, sortOrder: SortOrder | null, defaultSort?: IDefaultSort<IObject>[]) => {
    const sorts = defaultSort ? [...defaultSort] : [];
    if (sortField && !sorts.some((sort) => sort.field === sortField)) {
      sorts.unshift({ field: String(sortField), order: 'ascend' });
    }
    return sorts
      .map((sort) => {
        // Order sort of createdDate is always desc
        const sortDirection = sort.field === 'createdDate' ? QUERY_SORT.DESC : ANTD_TO_QUERY_SORT[sortOrder ?? sort.order];
        return `${sort.field}=${sortDirection}`;
      })
      .join(',');
  };

  return (
    <div className='space-y-[16px]'>
      <h3 className='text-2xl m-0'>{t('all_task_project:title_page')}</h3>
      <BaseModal
        openModal={isWarningDeadline}
        maskClosable={false}
        onCancel={() => setIsWarningDeadline(false)}
        contentElement={
          <div className='base-popup'>
            <div className='font-[700] text-[20px] text-lnk leading-[120%]'>{t('project:task_of_project:warning_change_status_deadline')}</div>
            <div className='font-[500] text-[14px] text-textGray mt-[16px] whitespace-pre-line leading-[160%]'>{t('common:MSG_P_030')}</div>
            <div className='flex-base-button grid-cols-2 gap-4 mt-[24px]'>
              <BaseButton onClick={() => setIsWarningDeadline(false)} size='medium' type='primary'>
                {t('common:yes')}
              </BaseButton>
            </div>
          </div>
        }
      />
      <TableFetch
        ref={tableRef}
        tableHeader={
          <FormProvider {...methods}>
            <div className='mb-4'>
              <FormBaseFilter
                searchBox={selectedKeys.length ? null : { placeholder: t('task:search_placeholder') ?? '' }}
                primaryAction={
                  selectedKeys.length
                    ? null
                    : {
                        label: t('button:filter'),
                        name: 'primary',
                        popoverContent: (
                          <PrimaryFilter
                            statuses={statuses}
                            taskTypes={CATEGORY_OPTIONS}
                            picListInfo={picListInfo}
                            setPicListInfo={setPicListInfo}
                            onSubmit={(data) => {
                              setValue('primary', data.primary);
                              updateFilter(data);
                            }}
                            onReset={resetPrimaryFilter}
                          />
                        ),
                        totalFiltered,
                        refControl: filterTableRef
                      }
                }
                secondaryAction={selectedKeys.length ? null : SECONDARY_FILTER}
                thirdAction={
                  selectedKeys.length
                    ? {
                        label: 'master_data:task:update_status',
                        showDropdownIcon: false,
                        refControl: filterTableRef,
                        onOpenChange: handleBulkUpdatePopoverOpenChange,
                        popoverContent: (
                          <div className='w-[520px] p-5'>
                            <div className='mb-4 text-xl font-semibold'>{t('master_data:task:update_status')}</div>

                            <div className='flex items-center gap-3'>
                              <div className='shrink-0'>
                                <BaseRadioGroups
                                  className={isDeadlineRequiredError ? 'task-bulk-update-radio-group' : ''}
                                  radioClassName={isDeadlineRequiredError ? 'task-deadline-option-checked' : 'task-deadline-option'}
                                  value={enableDeadline}
                                  onChange={(value) => {
                                    if (value === 'status' || value === 'deadline') {
                                      setEnableDeadline(value);
                                      setIsDeadlineRequiredError(false);
                                    }
                                  }}
                                  isVertical
                                  options={[
                                    {
                                      label: t('master_data:task:status_complete'),
                                      value: 'status'
                                    },
                                    {
                                      label: t('task:deadline'),
                                      value: 'deadline'
                                    }
                                  ]}
                                />
                              </div>

                              <div className='flex flex-1 min-w-0 items-start mt-10 gap-3'>
                                <div className='flex-1 min-w-0'>
                                  <FormInputDate
                                    name='updatedDateFrom'
                                    labelTx=''
                                    placeholder={t('master_data:task:placeholder_deadline') ?? ''}
                                    disabled={enableDeadline !== 'deadline'}
                                    triggerShowModal='both'
                                    format={FORMAT_DATE_EN}
                                    allowClear
                                    onChange={(date) => {
                                      if (date) {
                                        setIsDeadlineRequiredError(false);
                                      }
                                    }}
                                    customMessage={() => ({
                                      status: isDeadlineRequiredError ? 'error' : '',
                                      content: isDeadlineRequiredError ? (
                                        <div className='text-[14px] font-normal leading-[22px] text-negative'>{t('common:MSG_P_030')}</div>
                                      ) : (
                                        ''
                                      )
                                    })}
                                  />
                                </div>

                                <TimePicker
                                  className='h-[38px] w-[110px] shrink-0'
                                  format={FORMAT_TIME}
                                  placeholder={FORMAT_TIME}
                                  value={watchDeadlineTime}
                                  disabled={enableDeadline !== 'deadline'}
                                  allowClear
                                  locale={locale}
                                  onChange={(time) => {
                                    setValue('updatedDeadlineTime', time, {
                                      shouldDirty: true
                                    });
                                  }}
                                  getPopupContainer={() => document.body}
                                />
                              </div>
                            </div>

                            <div className='mt-4 flex justify-end gap-3'>
                              <BaseButton
                                type='tertiary'
                                size='medium'
                                onClick={() => {
                                  filterTableRef.current?.toggle(false);
                                  setIsDeadlineRequiredError(false);
                                }}
                              >
                                {t('button:cancel')}
                              </BaseButton>

                              <BaseButton type='primary' size='medium' onClick={handleUpdateMultiTask}>
                                {t('project:filter:btn_aply')}
                              </BaseButton>
                            </div>
                          </div>
                        )
                      }
                    : null
                }
                tagSection={selectedKeys.length ? null : renderTagSection()}
                onSubmit={(value, isChangeView) => updateFilter(value, isChangeView)}
              />
            </div>
          </FormProvider>
        }
        apiEndpoint={API.GET_TASKS_OF_ALL_PROJECTS}
        rowSelection={isInternalRole ? {} : null}
        keepHeaderOnSelection
        onSelectionChange={(keys, rows) => {
          setSelectedKeys(keys);
          setSelectedRows(rows);
        }}
        defaultSort={[
          { field: 'deadLine', order: 'ascend' },
          { field: 'createdDate', order: 'descend' }
        ]}
        customParamSort={customParamSort}
        apiMethod='POST'
        columns={COLUMNS}
        filterDefault={filter}
        scrollTable={{ x: 0, maxHeight: '100vh - 248px' }}
      />
    </div>
  );
};
